import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import { soldOutMapFromTiers } from "../lib/eventAvailability";
import {
  archiveEventsPastGracePeriod,
  filterEventsForPublicPage,
  filterPublicTicketEvents,
  getPublicEventsEmptyMessage,
} from "../lib/eventPublicStatus";
import { readPublicEventsCache, writePublicEventsCache } from "../utils/publicEventsCache";
import PublicPagination from "../components/PublicPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { PUBLIC_EVENTS_PAGE_SIZE, applyPagedSelect, totalPagesFromCount } from "../lib/adminPagination";
import { getManilaCalendarYmd } from "../lib/dateTime";

import {
  sanitizeSearchInput,
  SEARCH_NO_RESULTS_MESSAGE,
} from "../utils/searchUtils";

const SELECT_CLASS =
  "bg-[#0f0f0f] border border-red-700/60 text-white text-sm rounded-md px-3 py-2 min-w-[160px]";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [upcomingRange, setUpcomingRange] = useState("all");
  const [venueFilter, setVenueFilter] = useState("");
  const [availability, setAvailability] = useState("All");
  const [soldOutByEventId, setSoldOutByEventId] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const listTopRef = useRef(null);
  const [serverError, setServerError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [searchServerError, setSearchServerError] = useState("");
  const [searchInputError, setSearchInputError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = Math.max(1, Number(searchParams.get("page")) || 1);

  const EVENTS_PER_BATCH = PUBLIC_EVENTS_PAGE_SIZE;

  const fetchEvents = useCallback(async (options = {}) => {
    const { showRetryState = false } = options;

    if (showRetryState) {
      setRetrying(true);
    }

    setLoading(true);
    setServerError(false);

    const todayYmd = getManilaCalendarYmd();
    const keywordFromUrl = searchParams.get("q") || "";
    const sanitizedSearch = sanitizeSearchInput(keywordFromUrl);
    const keyword = sanitizedSearch.isValid ? sanitizedSearch.cleaned : "";

    let query = supabase
      .from("events")
      .select("*", { count: "exact" })
      .in("status", ["published", "upcoming"])
      .order("date", { ascending: true });

    if (filter === "Upcoming") {
      query = query.gte("date", todayYmd);
      if (upcomingRange === "week") {
        const weekEnd = new Date(`${todayYmd}T00:00:00+08:00`);
        weekEnd.setDate(weekEnd.getDate() + 7);
        query = query.lte("date", weekEnd.toISOString().slice(0, 10));
      } else if (upcomingRange === "month") {
        query = query.like("date", `${todayYmd.slice(0, 7)}%`);
      }
    } else if (filter === "Ongoing") {
      query = query.eq("date", todayYmd);
    } else {
      query = query.gte("date", todayYmd);
    }

    if (venueFilter) {
      query = query.eq("venue", venueFilter);
    }

    if (keyword) {
      const likeValue = `%${keyword}%`;
      query = query.ilike("title", likeValue);
    }

    const { data, error, count } = await applyPagedSelect(query, currentPage, EVENTS_PER_BATCH);

    if (error) {
      console.error("Error fetching events:", error);
      const cachedEvents = filterPublicTicketEvents(readPublicEventsCache());
      if (cachedEvents.length > 0) {
        setEvents(cachedEvents.slice(0, EVENTS_PER_BATCH));
        setTotalCount(cachedEvents.length);
      } else {
        setServerError(true);
        setEvents([]);
        setTotalCount(0);
      }
    } else {
      await archiveEventsPastGracePeriod(supabase, data || []);
      const liveEvents = filterPublicTicketEvents(data || []);
      setEvents(liveEvents);
      const fetchedCount = (data || []).length;
      const serverCount = Number(count) || 0;
      if (liveEvents.length < EVENTS_PER_BATCH || fetchedCount < EVENTS_PER_BATCH) {
        setTotalCount((currentPage - 1) * EVENTS_PER_BATCH + liveEvents.length);
      } else {
        setTotalCount(serverCount);
      }
      if (liveEvents.length > 0 && currentPage === 1 && !keyword) {
        writePublicEventsCache(liveEvents);
      }
      setServerError(false);
    }

    setLoading(false);
    if (showRetryState) {
      setRetrying(false);
    }
  }, [EVENTS_PER_BATCH, currentPage, filter, upcomingRange, venueFilter, searchParams]);

  useEffect(() => {
    fetchEvents();

    const subscription = supabase
      .channel("events_public")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "events" },
        () => {
          fetchEvents();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchEvents]);

  const handleRetry = () => {
    fetchEvents({ showRetryState: true });
  };

  const keywordFromUrl = searchParams.get("q") || "";
  const sanitizedSearch = sanitizeSearchInput(keywordFromUrl);
  const hasActiveSearch = Boolean(keywordFromUrl.trim());

  useEffect(() => {
    if (!keywordFromUrl.trim()) {
      setSearchInputError("");
      setSearchServerError("");
      setSearchLoading(false);
      return;
    }
    if (!sanitizedSearch.isValid) {
      setSearchInputError("Please enter a valid search keyword using letters and numbers.");
      setSearchServerError("");
      return;
    }
    setSearchInputError("");
    setSearchServerError("");
  }, [keywordFromUrl, sanitizedSearch.isValid]);

  const [venueOptions, setVenueOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("events")
      .select("venue")
      .neq("status", "archived")
      .limit(400)
      .then(({ data }) => {
        if (cancelled) return;
        const names = new Set();
        (data || []).forEach((event) => {
          const venue = String(event?.venue || "").trim();
          if (venue) names.add(venue);
        });
        setVenueOptions(Array.from(names).sort((a, b) => a.localeCompare(b)));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = events.map((event) => event.id).filter(Boolean);
    if (!ids.length) {
      setSoldOutByEventId({});
      return undefined;
    }

    let cancelled = false;
    const loadTiers = async () => {
      const { data, error } = await supabase
        .from("ticket_tiers")
        .select("event_id, quantity_available, quantity_sold")
        .in("event_id", ids);

      if (cancelled) return;
      if (error) {
        console.error("Error fetching ticket tiers:", error);
        setSoldOutByEventId({});
        return;
      }
      setSoldOutByEventId(soldOutMapFromTiers(data || []));
    };

    loadTiers();
    return () => {
      cancelled = true;
    };
  }, [events]);

  const statusFilteredEvents = filterEventsForPublicPage(events, {
    status: "All",
    venue: "",
    availability,
    upcomingRange: "all",
    soldOutByEventId,
  });

  const emptyMessage = getPublicEventsEmptyMessage({
    status: filter,
    venue: venueFilter,
    availability,
    upcomingRange: filter === "Upcoming" ? upcomingRange : "all",
    hasActiveSearch,
    searchNoResultsMessage: SEARCH_NO_RESULTS_MESSAGE,
  });

  const setPage = (page) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(page));
    setSearchParams(next, { replace: true });
  };

  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage: setPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  const totalPages = totalPagesFromCount(totalCount, EVENTS_PER_BATCH);

  const handleStatusFilter = (cat) => {
    setFilter(cat);
    if (cat !== "Upcoming") setUpcomingRange("all");
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white">

      {/* HERO */}
      <section className="relative h-[45vh] flex flex-col items-center justify-center overflow-hidden">

        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/assets/home_featured_bg.png')" }}
        />

        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10 text-center">
          <img
            src="/assets/echojham.logo2.png"
            alt="logo"
            className="h-10 mx-auto mb-4 opacity-80"
          />

          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
            Shows and Concerts
          </h1>
        </div>
      </section>

      {/* FILTER */}
      <div className="flex flex-col items-center gap-6 mt-10 mb-16 px-6">
        <div className="flex justify-center gap-8">
          {["All", "Upcoming", "Ongoing"].map((cat) => (
            <button
              key={cat}
              onClick={() => handleStatusFilter(cat)}
              className={`px-6 py-2 rounded-md text-sm font-semibold transition
                ${
                  filter === cat
                    ? "bg-red-600 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <label className="flex flex-col gap-1 text-xs text-gray-400">
            Venue
            <select
              value={venueFilter}
              onChange={(event) => {
                setVenueFilter(event.target.value);
                setPage(1);
              }}
              className={SELECT_CLASS}
            >
              <option value="">All venues</option>
              {venueOptions.map((venue) => (
                <option key={venue} value={venue}>
                  {venue}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-gray-400">
            Availability
            <select
              value={availability}
              onChange={(event) => {
                setAvailability(event.target.value);
                setPage(1);
              }}
              className={SELECT_CLASS}
            >
              <option value="All">All</option>
              <option value="Available">Available</option>
              <option value="Sold out">Sold out</option>
            </select>
          </label>

          {filter === "Upcoming" && (
            <label className="flex flex-col gap-1 text-xs text-gray-400">
              Date
              <select
                value={upcomingRange}
                onChange={(event) => {
                  setUpcomingRange(event.target.value);
                  setPage(1);
                }}
                className={SELECT_CLASS}
              >
                <option value="all">All upcoming</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
              </select>
            </label>
          )}
        </div>
      </div>

      {/* EVENTS GRID */}
      <section ref={listTopRef} className="max-w-6xl mx-auto px-6 pb-24">

        {/* SERVER ERROR STATE */}
        {serverError && (
          <div className="text-center py-20">
            <p className="text-gray-400 mb-6">
              Events currently unavailable. Please try again.
            </p>
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white font-semibold rounded-md transition"
            >
              {retrying ? "Retrying..." : "Retry"}
            </button>
          </div>
        )}

        {loading && events.length === 0 && !serverError && (
          <div className="text-center py-20 text-gray-400">
            Loading...
          </div>
        )}

        {!serverError && (events.length > 0 || !loading) && (
          <BusyTable show={showSkeleton && events.length > 0} tone="public">
            {statusFilteredEvents.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 justify-items-center">
                  {statusFilteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="w-[260px] bg-[#0f0f0f] border border-red-700 rounded-xl p-4 shadow-[0_0_20px_rgba(255,0,0,0.15)] hover:shadow-[0_0_30px_rgba(255,0,0,0.35)] transition"
                    >
                      {/* Poster */}
                      <div className="relative overflow-hidden rounded-lg mb-4">
                        <img
                          src={event.image_url || "/assets/featured_event.jpg"}
                          alt={event.title}
                          className="w-full h-[340px] object-cover"
                        />
                        {soldOutByEventId[String(event.id)] && (
                          <span className="absolute top-3 right-3 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-1 rounded">
                            Sold out
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-sm font-bold uppercase mb-2">
                        {event.title}
                      </h3>

                      {/* Date */}
                      <div className="flex justify-between text-xs text-red-500 font-semibold mb-4">
                        <span>{event.date || "Date TBA"}</span>
                        <span>{event.time || "Time TBA"}</span>
                      </div>

                      {/* Button */}
                      <button
                        onClick={() => navigate(`/event/${event.id}`)}
                        className="w-full py-2 bg-red-600 hover:bg-red-700 text-xs font-bold uppercase rounded-md transition"
                      >
                        Buy Tickets
                      </button>
                    </div>
                  ))}
                </div>

                {totalPages > 1 ? (
                  <PublicPagination
                    className="mt-16"
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                  />
                ) : null}
              </>
            ) : (
              <div className="text-center py-20 text-gray-500">
                {loading ? "Loading..." : emptyMessage}
              </div>
            )}
          </BusyTable>
        )}

        {!loading && !serverError && hasActiveSearch && searchLoading && (
          <div className="text-center py-8 text-gray-400 text-sm">Searching events...</div>
        )}

        {!loading && !serverError && searchInputError && (
          <div className="text-center py-4 text-red-400 text-sm">{searchInputError}</div>
        )}

        {!loading && !serverError && !searchInputError && searchServerError && (
          <div className="text-center py-4 text-red-400 text-sm">{searchServerError}</div>
        )}
      </section>
    </div>
  );
};

export default Events;